<template>
    <div class="WebhooksTable text-eidDarkBlue">
        <Table :self-contained="true">
            <template #toolbar>
                <span
                    data-test-id="WebhooksSection__sectionTitle"
                    class="WebhooksTable__toolbarTitle font-medium"
                >
                    Webhooks
                </span>
            </template>
            <template #toolbarEnd>
                <NewButton
                    v-if="indexOfEditWebhook == null && !updating"
                    data-test-id="WebhooksTable__addButton"
                    variant="fill"
                    color="primary"
                    @click="onClickAddWebhook"
                >
                    <EidIcon :icon-src="faPlus" />
                    ADD WEBHOOK
                </NewButton>
            </template>
            <template #head>
                <HeadCell
                    data-test-id="WebhooksTable__nameHeadCell"
                    class="text-eidDarkBlue-50 font-semibold"
                >
                    Event
                </HeadCell>
                <HeadCell
                    data-test-id="WebhooksTable__urlHeadCell"
                    class="text-eidDarkBlue-50 font-semibold"
                >
                    Webhook URL
                </HeadCell>
                <HeadCell
                    data-test-id="WebhooksTable__actionsHeadCell"
                    class="text-eidDarkBlue-50 font-semibold"
                    aria-label="Actions"
                />
            </template>
            <WebhooksTableRow
                v-for="(webhook, index) in localWebhooks"
                :key="index"
                :webhook="webhook"
                :hide-actions="indexOfEditWebhook != null && indexOfEditWebhook !== index"
                :updating="updating"
                :is-editing="indexOfEditWebhook === index"
                :schema="rowSchema"
                @change="onWebhookChange($event,index)"
                @edit="onEditStatusChange($event, index)"
                @cancel-edit="cancelEdit(index)"
                @delete="onDeleteWebhook(index)"
            />
        </Table>
        <template v-if="!localWebhooks || localWebhooks.length === 0">
            <div
                data-test-id="WebhooksTable__noWebhooksText"
                class="WebhooksTable__emptyContainer px-7 text-eidDarkBlue-50"
            >
                You can configure API settings to integrate with systems you are already using.
            </div>
        </template>

        <ConfirmationModal
            v-if="indexOfDeletingWebhook != null"
            additional-class-name="ConfirmDeleteCollateralModal"
            title="Are you sure you want to delete the webhook?"
            yes="Delete"
            no="Cancel"
            :destructive="true"
            :progress="deleting"
            @input="onConfirmDelete"
        >
            Deleting an item will permanently remove it from the system.
        </ConfirmationModal>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faPlus } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import { InsuranceWebhook, InsuranceWebhookType } from '@evidentid/tprm-portal-lib/models/api-settings';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import WebhooksTableRow
        from '@/modules/api-settings/components/webhooks-table-row/WebhooksTableRow.vue';
    import {
        buildWebhookTableRowSchema,
    } from '@/modules/api-settings/utils/buildWebhookTableRowSchema/buildWebhookTableRowSchema';

    export default defineComponent({
        name: 'WebhooksTable',
        components: {
            WebhooksTableRow,
            NewButton,
            HeadCell,
            Table,
            ConfirmationModal,
            EidIcon,
        },
        props: {
            webhooks: {
                type: Array as PropType<InsuranceWebhook[]>,
                default: (): InsuranceWebhook[] => [] as InsuranceWebhook[],
            },
            webhookTypes: {
                type: Array as PropType<InsuranceWebhookType[]>,
                default: (): InsuranceWebhookType[] => [] as InsuranceWebhookType[],
            },
            updating: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            deleting: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'change', 'delete' ],
        data() {
            return {
                faPlus,
                indexOfDeletingWebhook: null as number | null,
                localWebhooks: [] as InsuranceWebhook[],
                indexOfEditWebhook: null as number | null,
                rowSchema: buildWebhookTableRowSchema(this.webhookTypes),
            };
        },
        watch: {
            deleting(newValue: boolean) {
                if (!newValue) {
                    this.indexOfDeletingWebhook = null;
                }
            },
            webhooks: {
                immediate: true,
                handler(newValue?: boolean) {
                    if (newValue) {
                        this.localWebhooks = [ ...this.webhooks ];
                    }
                },
            },
            webhookTypes: {
                immediate: true,
                handler() {
                    this.rowSchema = buildWebhookTableRowSchema(this.webhookTypes);
                },
            },
        },
        methods: {
            onClickAddWebhook(): void {
                this.localWebhooks.push({} as InsuranceWebhook);
                this.indexOfEditWebhook = this.localWebhooks.length - 1;
            },
            onWebhookChange(webhook: InsuranceWebhook, listIndex: number): void {
                this.localWebhooks = [
                    ...this.localWebhooks.slice(0, listIndex),
                    webhook,
                    ...this.localWebhooks.slice(listIndex + 1),
                ];
                this.$emit('change', webhook);
            },
            onDeleteWebhook(webhookIndex: number): void {
                if (this.localWebhooks[webhookIndex]?.id) {
                    this.indexOfDeletingWebhook = webhookIndex;
                } else {
                    // if no id, it's new input, simply remove from local list
                    this.localWebhooks = [
                        ...this.localWebhooks.slice(0, webhookIndex),
                        ...this.localWebhooks.slice(webhookIndex + 1),
                    ];
                }
            },
            onConfirmDelete(confirmed: boolean): void {
                if (!confirmed) {
                    this.indexOfDeletingWebhook = null;
                } else if (this.indexOfDeletingWebhook != null) {
                    const toDelete = { ...this.localWebhooks[this.indexOfDeletingWebhook] };
                    this.$emit('delete', toDelete);
                    this.localWebhooks = [
                        ...this.localWebhooks.slice(0, this.indexOfDeletingWebhook),
                        ...this.localWebhooks.slice(this.indexOfDeletingWebhook + 1),
                    ];
                }
            },
            onEditStatusChange(editing: boolean, webhookIndex: number): void {
                this.indexOfEditWebhook = editing ? webhookIndex : null;
            },

            cancelEdit(webhookIndex: number): void {
                const isExistedWebhook = Boolean(this.localWebhooks[webhookIndex]?.id);
                if (isExistedWebhook) {
                    this.localWebhooks = [
                        ...this.localWebhooks.slice(0, webhookIndex),
                        { ...this.localWebhooks[webhookIndex] },
                        ...this.localWebhooks.slice(webhookIndex + 1),
                    ];
                } else {
                    this.localWebhooks.pop();
                }
            },
        },
    });
</script>
